import { type TypographyVariant } from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import type { ImageProps } from "../types";
import { CdnImage } from "./common/CdnImage";
import { MdaiDivider } from "./MdaiDivider";
import { MdaiLink } from "./MdaiLink";

interface HeroButtonsProps {
  text?: string;
  href: string;
  variant?: "text" | "outlined" | "contained";
  onClick?: () => void;
}
export interface HeroProps {
  tag?: string;
  title: string;
  titleVariant?: TypographyVariant;
  titleFlip?: boolean;
  subtitle?: string;
  align?: "start" | "center" | "right";
  image?: ImageProps & { href?: string; caption?: string; aspectRatio?: string };
  description?: string | ReactNode;
  buttons?: HeroButtonsProps[];
  divider?: boolean;
}

export const HeroComponent = ({
  tag,
  title,
  titleVariant = "h2",
  titleFlip = false,
  subtitle,
  align,
  image,
  description,
  buttons,
  divider = true,
}: HeroProps) => {
  return (
    <>
      <Stack
        direction={{
          sm: "column",
          lg: image ? (titleFlip ? "row-reverse" : "row") : "column",
        }}
        spacing={2}
        gap={image?.flip ? 6 : 10}
        alignItems={"center"}
        justifyContent={{ lg: "center", sm: "space-between" }}
        mt={image ? 5 : 0}
        mb={divider ? 0 : 8}
      >
        {image && (
          <Stack
            width={{ sm: 250, md: 320, lg: "25%" }}
            sx={{
              position: { sm: "absolute", lg: "inherit" },
            }}
          >
            {image.href ? (
              <Link
                href={image.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "block" }}
              >
                <CdnImage
                  alt={image.alt ?? title}
                  src={image.src}
                  sx={{
                    width: { sm: "95%", lg: image.width ?? 350 },
                    aspectRatio: image.aspectRatio ?? "1 / 1",
                    transform: image.flip ? "scaleX(-1)" : "none",
                  }}
                />
              </Link>
            ) : (
              <CdnImage
                alt={image.alt ?? title}
                src={image.src}
                sx={{
                  width: { sm: "95%", lg: image.width ?? 350 },
                  aspectRatio: image.aspectRatio ?? "1 / 1",
                  transform: image.flip ? "scaleX(-1)" : "none",
                }}
              />
            )}
            {image.caption && (
              <Link
                href={image.href}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                sx={{ mt: 1, display: "block", textAlign: "center" }}
              >
                {image.caption}
              </Link>
            )}
          </Stack>
        )}
        <Stack
          alignItems={{ sm: "center", lg: align ?? "start" }}
          gap={2}
          pt={{ sm: image && "200px", md: image && "250px", lg: 0 }}
          width={{ sm: "100%", lg: image ? "75%" : "100%" }}
        >
          {tag && (
            <Chip
              color="primary"
              label={tag}
              sx={{
                textAlign: align ?? "start",
                zIndex: 1,
              }}
            />
          )}
          <Typography
            variant={titleVariant}
            textAlign={{ sm: "center", lg: align ?? "start" }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Box sx={{ width: "100%" }}>
              <Typography
                variant="h4"
                sx={{
                  color: "#FFF",
                  mb: "24px",
                  whiteSpace: "pre-line",
                }}
                textAlign={{ sm: "center", lg: align ?? "start" }}
              >
                {subtitle}
              </Typography>
            </Box>
          )}
          {typeof description === "string" ? (
            <Typography
              variant="subtitle1"
              textAlign={align ?? "start"}
              maxWidth={800}
            >
              {description}
            </Typography>
          ) : (
            <>{description}</>
          )}
          {buttons && buttons.length > 0 && (
            <Stack
              direction={{ sm: "column", md: "row" }}
              alignItems={{ sm: "center", lg: "flex-start" }}
              spacing={2}
            >
              {buttons.map(({ text, href, variant, onClick }) => (
                <MdaiLink
                  key={text}
                  label={text}
                  href={href}
                  type="button"
                  variant={variant}
                  onClick={onClick}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
      {divider && <MdaiDivider />}
    </>
  );
};
